<template lang="pug">
  .feed-post-reactions
    .emoji-action-wrapper(
      @mouseenter="showEmojiPicker = true"
      @mouseleave="showEmojiPicker = false"
    )
      button.emoji-button(
        :class="{ 'is-active': hasUserReaction, 'has-text': !hasUserReaction}"
        :disabled="isLoadingReaction"
        :title="hasUserReaction ? 'Remove reaction' : 'React'"
        @click="handleEmojiButtonClick"
      )
        WtSvgIcon(
          :key="reactionsData.userReaction"
          :icon="hasUserReaction ? emojiMap[reactionsData.userReaction] : 'grey_applaud'"
          width="20px"
          height="20px"
        )
        span(v-if="!hasUserReaction") Applause


      transition(name="slideDown")
        .emoji-menu(v-if="showEmojiPicker && !isLoadingReaction")
          button.emoji-item(
            v-for="emoji in availableEmojis"
            :key="emoji"
            :class="{ 'is-active': reactionsData.userReaction === emoji }"
            @click="toggleReaction(emoji)"
            :title="emojiLabels[emoji]"
          )
            WtSvgIcon(:icon="emojiMap[emoji]" width="20px" height="20px")

    BModal(
      :active.sync="showReactionsModal"
      has-modal-card
      trap-focus
      :can-cancel="['escape', 'outside']"
    )
      .modal-card.reactions-modal
        header.modal-card-head
          .header-content
            .header-top
              p.emojis-popup-title All Reactions ({{ reactionsData.total }})
            .emoji-tabs-wrapper
              button.emoji-tab(
                :class="{ 'is-active': selectedEmojiTab === null }"
                @click="selectedEmojiTab = null"
                title="All reactions"
              )
                span All
              .emoji-tabs-scroll
                button.emoji-tab(
                  v-for="emoji in emojiTabs"
                  :key="emoji"
                  :class="{ 'is-active': selectedEmojiTab === emoji }"
                  @click="toggleEmojiTab(emoji)"
                  :title="emojiLabels[emoji]"
                )
                  WtSvgIcon(:icon="emojiMap[emoji]" width="20px" height="20px")
                  span.tab-count(
                    :class="{ 'is-active': selectedEmojiTab === emoji }"
                  ) {{ getEmojiCount(emoji) }}
              button.delete(aria-label="Close" @click="showReactionsModal = false")
        section.modal-card-body
          .loading-state(v-if="isLoadingDetailedReactions")
            p Loading reactions...
          .reactions-content(v-else-if="filteredUsers.length > 0")
            .users-list
              .user-row(v-for="user in filteredUsers" :key="user.userId || user.id" )
                .user-avatar
                  EntityAvatar(
                    v-if="user.avatar"
                    :image="user.avatar"
                    size-class="is-32x32"
                    is-rounded
                  )
                  .avatar-placeholder(v-else)
                    span {{ getUserInitial(user) }}
                .user-name
                  span {{ getUserFullName(user) }}
                .user-reaction-badge
                  WtSvgIcon(
                    :icon="emojiMap[user.emoji]"
                    width="18px"
                    height="18px"
                    :title="emojiLabels[user.emoji]"
                  )
          .empty-state(v-else)
            p No reactions yet
</template>

<script>
import { mapActions } from "vuex";
import EntityAvatar from "@/components/WtEntityAvatar/WtEntityAvatar";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";
import Vue from "vue";

const EMOJI_MAP = {
  applaud: "applaud",
  happy: "smiley",
  love: "love",
  like: "like",
  good_luck: "luck",
  laugh: "laugh"
};

const EMOJI_LABELS = {
  applaud: "Applaud",
  happy: "Happy",
  love: "Love",
  like: "Like",
  good_luck: "Good Luck",
  laugh: "Laugh"
};

export default {
  name: "FeedPostReactions",

  components: {
    EntityAvatar,
    WtSvgIcon
  },

  props: {
    post: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      isLoadingReaction: false,
      showEmojiPicker: false,
      showReactionsModal: false,
      emojiMap: EMOJI_MAP,
      emojiLabels: EMOJI_LABELS,
      availableEmojis: [
        "applaud",
        "happy",
        "love",
        "like",
        "good_luck",
        "laugh"
      ],
      detailedReactions: [],
      isLoadingDetailedReactions: false,
      selectedEmojiTab: null
    };
  },

  computed: {
    reactionsData() {
      const data = this._.get(this.post, "reactions", {
        total: 0,
        emojis: [],
        userReaction: null,
        details: []
      });
      return data;
    },

    hasUserReaction() {
      return this.reactionsData.userReaction !== null;
    },

    groupedReactions() {
      const grouped = {};
      const emojiOrder = [
        "applaud",
        "happy",
        "love",
        "like",
        "good_luck",
        "laugh"
      ];

      this.detailedReactions.forEach(reaction => {
        if (!grouped[reaction.emoji]) {
          grouped[reaction.emoji] = [];
        }
        grouped[reaction.emoji].push(reaction);
      });

      const sorted = {};
      emojiOrder.forEach(emoji => {
        if (grouped[emoji]) {
          sorted[emoji] = grouped[emoji];
        }
      });

      return sorted;
    },

    emojiTabs() {
      const emojiOrder = [
        "applaud",
        "happy",
        "love",
        "like",
        "good_luck",
        "laugh"
      ];
      return emojiOrder.filter(emoji => this.groupedReactions[emoji]);
    },

    filteredUsers() {
      // If no emoji tab is selected, show all users
      if (this.selectedEmojiTab === null) {
        return this.detailedReactions;
      }
      // If an emoji tab is selected, show only users for that emoji
      return this.groupedReactions[this.selectedEmojiTab] || [];
    }
  },

  watch: {
    showReactionsModal(newVal) {
      if (newVal && this.reactionsData.total > 0) {
        this.fetchDetailedReactions();
      } else if (!newVal) {
        this.selectedEmojiTab = null;
      }
    }
  },

  methods: {
    ...mapActions("feed", ["addFeedPostReaction", "removeFeedPostReaction"]),

    getUserInitial(user) {
      const name = user.firstname || user.username;
      return name.charAt(0).toUpperCase();
    },

    getEmojiCount(emoji) {
      return this.groupedReactions[emoji]?.length || 0;
    },

    toggleEmojiTab(emoji) {
      // Click on same emoji to deselect it and show all
      if (this.selectedEmojiTab === emoji) {
        this.selectedEmojiTab = null;
      } else {
        this.selectedEmojiTab = emoji;
      }
    },

    openReactionsModal() {
      this.showReactionsModal = true;
    },

    async fetchDetailedReactions() {
      if (this.isLoadingDetailedReactions || this.post.id === undefined) {
        return;
      }

      this.isLoadingDetailedReactions = true;
      try {
        const response = await Vue.axios.get(
          `feed/posts/${this.post.id}/reactions`
        );

        let data = this._.get(response, "data.data", []);
        if (!Array.isArray(data)) {
          data = this._.get(response, "data", []);
        }

        this.detailedReactions = data;
      } catch (error) {
        this.detailedReactions = [];
      } finally {
        this.isLoadingDetailedReactions = false;
      }
    },

    async handleEmojiButtonClick() {
      if (this.hasUserReaction) {
        await this.toggleReaction(this.reactionsData.userReaction);
      } else {
        this.showEmojiPicker = !this.showEmojiPicker;
      }
    },

    async toggleReaction(emoji) {
      if (this.isLoadingReaction) {
        return;
      }

      this.isLoadingReaction = true;
      this.showEmojiPicker = false;

      try {
        if (this.reactionsData.userReaction === emoji) {
          await this.removeFeedPostReaction({
            postId: this.post.id,
            emoji
          });
        } else {
          await this.addFeedPostReaction({
            postId: this.post.id,
            emoji
          });
        }
      } finally {
        this.isLoadingReaction = false;
      }
    },

    getUserFullName(user) {
      const fullName = [user.firstname, user.lastname]
        .filter(name => name)
        .join(" ");

      return fullName || user.username || "User";
    }
  }
};
</script>

<style lang="sass" scoped>
.feed-post-reactions
  display: flex
  align-items: center
  gap: 8px
  margin: 0

  .reactions-group
    .reactions-button
      display: inline-flex
      align-items: center
      gap: 6px
      padding: 4px 10px
      border-radius: 16px
      cursor: pointer
      transition: all 0.15s ease
      user-select: none

      &:hover
        background: #E4E6EB
        transform: translateY(-1px)

      .emojis-display
        display: flex
        gap: 2px
        align-items: center

        .emoji-item
          font-size: 16px
          line-height: 1
          display: flex
          align-items: center

      .total-count
        font-size: 12px
        font-weight: 600
        color: #5D6472

  .emoji-action-wrapper
    position: relative
    display: inline-flex
    align-items: center

    .emoji-button
      display: flex
      align-items: center
      justify-content: center
      width: 32px
      height: 32px
      border-radius: 6px
      border: none
      background: transparent
      cursor: pointer
      transition: all 0.15s ease
      padding: 0

      &:hover:not(:disabled)
        opacity: 0.7

      &.has-text
        height: 32px
        width: auto
        padding: 0 8px
        gap: 6px

      &:disabled
        opacity: 0.5
        cursor: not-allowed

    .emoji-menu
      position: absolute
      bottom: 100%
      left: 0
      display: flex
      gap: 6px
      background: white
      border: 1px solid #E8EBEF
      border-radius: 8px
      padding: 6px
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1)
      margin-bottom: 8px
      z-index: 10
      animation: slideDown 0.15s ease

      .emoji-item
        display: flex
        align-items: center
        justify-content: center
        width: 32px
        height: 32px
        border-radius: 6px
        border: 1px solid transparent
        background: transparent
        cursor: pointer
        font-size: 16px
        transition: all 0.15s ease
        padding: 0

        &:hover
          background: #F8F9FA
          border-color: #D0D5DC

        &.is-active
          background: #E8F0FF
          border-color: grey

@keyframes slideDown
  from
    opacity: 0
    transform: translateY(-4px)
  to
    opacity: 1
    transform: translateY(0)

.modal-card.reactions-modal
  max-width: 500px
  width: 100%
  border-radius: 12px

  .modal-card-head
    background: white
    border-bottom: 1px solid #E8EBEF
    padding: 16px 20px
    display: block

    .header-content
      display: flex
      flex-direction: row
      align-items: center
      justify-content: center
      gap: 12px

      .header-top
        display: flex
        align-items: center
        justify-content: space-between

        .emojis-popup-title
          font-weight: 600
          font-size: 16px
          color: #1B1D20
          margin: 0

        .delete
          position: static
          width: 24px
          height: 24px
          margin-left: auto

      .emoji-tabs-wrapper
        display: flex
        align-items: center
        gap: 8px
        overflow: hidden

        .emoji-tabs-scroll
          display: flex
          align-items: center
          gap: 6px
          overflow-x: auto
          flex: 1
          scrollbar-width: none
          -ms-overflow-style: none
          padding-bottom: 2px

          &::-webkit-scrollbar
            display: none

        .emoji-tab
          display: flex
          align-items: center
          gap: 6px
          padding: 6px 12px
          border-radius: 20px
          border: 1px solid #E8EBEF
          background: white
          cursor: pointer
          transition: all 0.2s ease
          flex-shrink: 0
          white-space: nowrap
          font-size: 13px
          font-weight: 500
          color: #5D6472

          &:hover
            border-color: #2972F0
            background: #F0F7FF
            color: #2972F0

          &.is-active
            background: #2972F0
            border-color: #2972F0
            color: white

            svg
              filter: brightness(0) invert(1)

            .tab-count
              color: white

          .tab-count
            font-size: 12px
            font-weight: 600
            color: #5D6472

  .modal-card-body
    padding: 0
    background: white
    display: flex
    flex-direction: column
    max-height: 400px

    .reactions-content
      display: flex
      flex-direction: column
      height: 100%

      .users-list
        flex: 1
        overflow-y: auto
        display: flex
        flex-direction: column

        .user-row
          display: flex
          align-items: center
          gap: 12px
          padding: 12px 20px
          border-bottom: 1px solid #F0F2F5
          transition: background 0.2s ease

          &:last-child
            border-bottom: none

          &:hover
            background: #F8F9FA

          .user-avatar
            display: flex
            align-items: center
            justify-content: center
            flex-shrink: 0

            .avatar-placeholder
              display: flex
              align-items: center
              justify-content: center
              width: 32px
              height: 32px
              border-radius: 50%
              background: linear-gradient(135deg, #2972F0 0%, #1E5BD0 100%)
              color: white
              font-weight: 600
              font-size: 14px

          .user-name
            font-size: 14px
            color: #1B1D20
            font-weight: 500
            word-break: break-word
            flex: 1
            min-width: 0

          .user-reaction-badge
            display: flex
            align-items: center
            justify-content: center
            width: 28px
            height: 28px
            border-radius: 6px
            flex-shrink: 0
            margin-left: auto

            svg
              color: #5D6472

    .loading-state
      display: flex
      align-items: center
      justify-content: center
      min-height: 200px
      color: #8A8E96

    .empty-state
      display: flex
      align-items: center
      justify-content: center
      min-height: 200px
      color: #8A8E96
      font-size: 14px
</style>
